import { Link2Off, LogIn, RefreshCw } from 'lucide-react'

import type { InstagramDestination } from '@/api/endpoints/destinations'
import { Button } from '@/components/ui/Button'
import { Chip } from '@/components/ui/Chip'
import {
  ACCOUNT_STATUS,
  accountTypeLabel,
  blockReasonText,
  displayHandle,
  expiresSoon,
  expiryText,
} from '../../destinations/model'
import { PlatformCard, PlatformDetail } from '../../PlatformCard'
import styles from '../../PlatformCard.module.scss'

export interface InstagramAccountCardProps {
  destination: InstagramDestination
  /** Uma ação em andamento (desconectar): trava os botões. */
  busy?: boolean
  onConnect: () => void
  onDisconnect: () => void
  now?: Date
}

/**
 * O cartão do Instagram na seção Plataformas: a conta conectada (@, tipo,
 * status, quando a credencial vence) e os botões Conectar, Reconectar e
 * Desconectar. O dono não digita nada no Prism: ele entra no instagram.com com
 * o perfil e aprova. Presentacional — o contêiner cuida das chamadas.
 */
export function InstagramAccountCard({
  destination,
  busy = false,
  onConnect,
  onDisconnect,
  now = new Date(),
}: InstagramAccountCardProps) {
  const { account, publisher } = destination
  const registered = publisher.registered
  const active = account?.status === 'active'

  const status = !registered ? (
    <Chip>Indisponível neste servidor</Chip>
  ) : account ? (
    <Chip variant={ACCOUNT_STATUS[account.status].variant}>{ACCOUNT_STATUS[account.status].label}</Chip>
  ) : (
    <Chip variant="warning">Desconectado</Chip>
  )

  const expiry = account ? expiryText(account.expires_at, now) : ''
  const soon = active && expiresSoon(account.expires_at, now)

  return (
    <PlatformCard
      platform="instagram"
      kind="destino"
      status={status}
      details={
        account ? (
          <>
            <PlatformDetail label="Perfil">{displayHandle(account.username)}</PlatformDetail>
            <PlatformDetail label="Tipo de conta">{accountTypeLabel(account.account_type)}</PlatformDetail>
            {expiry ? <PlatformDetail label="Credencial">{expiry}</PlatformDetail> : null}
            <PlatformDetail label="Destino">{destination.enabled ? 'ligado' : 'desligado'}</PlatformDetail>
          </>
        ) : undefined
      }
      actions={
        registered ? (
          account ? (
            <>
              <Button
                size="sm"
                variant={active && !soon ? 'secondary' : 'primary'}
                leadingIcon={<RefreshCw />}
                disabled={busy}
                onClick={onConnect}
              >
                Reconectar
              </Button>
              <Button size="sm" variant="ghost" leadingIcon={<Link2Off />} disabled={busy} onClick={onDisconnect}>
                Desconectar
              </Button>
            </>
          ) : (
            <Button size="sm" leadingIcon={<LogIn />} disabled={busy} onClick={onConnect}>
              Conectar Instagram
            </Button>
          )
        ) : undefined
      }
    >
      {!registered ? <p className={styles.note}>{blockReasonText('no_publisher')}</p> : null}
      {account && account.status !== 'active' ? (
        <p className={styles.note} role="alert">
          {account.status === 'expired'
            ? 'A credencial expirou e nada é publicado no Instagram até você reconectar.'
            : 'O Instagram revogou o acesso do Prism. Nada é publicado até você reconectar.'}
        </p>
      ) : null}
      {soon && expiry ? (
        <p className={styles.note} role="alert">
          A credencial {expiry} e a renovação automática não aconteceu. Reconecte para a publicação não parar.
        </p>
      ) : null}
      {registered && active && !publisher.enabled ? (
        <p className={styles.note}>{blockReasonText('publisher_off')}</p>
      ) : null}
      {registered && active && !destination.enabled ? (
        <p className={styles.note}>
          Conectado, mas ainda não publica: ligue o destino no Scheduler, em editar o canal → aba Destinos.
        </p>
      ) : null}
      {registered && !active ? <InstagramPrerequisites /> : null}
    </PlatformCard>
  )
}

/** O que precisa estar pronto antes do botão Conectar — feito uma vez, fora do Prism. */
export function InstagramPrerequisites() {
  return (
    <>
      <p className={styles.note}>Antes de conectar, no Instagram:</p>
      <ol className={styles.steps}>
        <li>O perfil precisa ser profissional (Criador ou Empresa). Conta pessoal é recusada.</li>
        <li>
          O perfil precisa ter aceitado o convite de testador do app do Prism: Instagram → Configurações → Apps e sites →
          Convites de testador.
        </li>
        <li>Na tela do Instagram, entre com o perfil certo e deixe todas as permissões marcadas.</li>
      </ol>
      <p className={styles.note}>O Prism não pede senha nem token: tudo acontece na tela do Instagram.</p>
    </>
  )
}
